import { GameDocumentContext } from '../../contexts/game-document';
import { useCallback, useContext, useEffect, useState } from 'react';
import Toolbar from './toolbar';
import { Container } from 'react-bootstrap';
import { TabContentCG } from '../../components/tabs';
import {
  InGameSetting,
  DesignerSetting,
  PreGameSetting
} from '../../components/settings';
import { TabStripSelectEventArguments } from '@progress/kendo-react-layout';
import { ContentItem } from '../../types/tab-content';
import {
  AddPreGameSettingAsync,
  AddInGameSettingAsync,
  AddDesignerSettingAsync,
  UpdatePreGameSettingAsync,
  UpdateInGameSettingAsync
} from '../../utils/game-document/settings';
import { UpdateGameDocState } from '../../utils/game-document';
import { GameDocument } from '../../types/game-document';
import { RadioButtonChangeEvent } from '@progress/kendo-react-inputs';
import { matchPath, useLocation } from 'react-router-dom';
import { useBreadcrumb } from '../../contexts/breadcrumb';

const GameSettings = () => {
  const [state, setState] = useContext(GameDocumentContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<number>(0);

  const handleTabSelect = (e: TabStripSelectEventArguments) => {
    setSelectedTab(e.selected);
  };

  const onChangeHandler = useCallback(
    (updatedGameDocument: GameDocument) => {
      setState((state) => UpdateGameDocState(state, updatedGameDocument));
    },
    [setState]
  );

  const handlePreAndInGameSetting = (
    type: 'PreGame' | 'InGame',
    { name, value }: { name: string; value: boolean }
  ) => {
    if (type === 'PreGame') {
      const preGameSetting = state.gameDocument?.settings.preGame!;

      UpdatePreGameSettingAsync(state.gameDocument!, {
        ...preGameSetting,
        [name]: value
      }).then((updatedGameDocument) => {
        onChangeHandler(updatedGameDocument);
      });
    } else {
      const inGameSetting = state.gameDocument?.settings.inGame!;

      UpdateInGameSettingAsync(state.gameDocument!, {
        ...inGameSetting,
        [name]: value
      }).then((updatedGameDocument) => {
        onChangeHandler(updatedGameDocument);
      });
    }
  };

  const setGameSetting = () => {
    if (!state.gameDocument) return;

    setIsLoading(true);
    !state.gameDocument.settings?.designer &&
      AddDesignerSettingAsync(state.gameDocument).then((updated) =>
        onChangeHandler(updated)
      );

    !state.gameDocument.settings?.preGame &&
      AddPreGameSettingAsync(
        state.gameDocument,
        'Pre-Game Setting',
        'Pre-Game Setting'
      ).then((updated) => onChangeHandler(updated));

    !state.gameDocument.settings?.inGame &&
      AddInGameSettingAsync(
        state.gameDocument,
        'In-Game Setting',
        'In-Game Setting'
      ).then((updated) => onChangeHandler(updated));

    setIsLoading(false);
  };

  useEffect(() => {
    if (state.gameDocument) {
      // Set the page title.
      document.title = `Settings - ${state.gameDocument.name}`;
      setGameSetting();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.gameDocument?.version]);

  const items: ContentItem[] = [
    {
      title: 'Designer',
      content: <DesignerSetting state={state} onChange={onChangeHandler} />
    },
    {
      title: 'Pre-Game',
      content: (
        <PreGameSetting
          state={state.gameDocument?.settings.preGame!}
          onChange={(name, value) =>
            handlePreAndInGameSetting('PreGame', { name, value })
          }
        />
      )
    },
    {
      title: 'In-Game',
      content: (
        <InGameSetting
          state={state.gameDocument?.settings.inGame!}
          onChange={(name, value) =>
            handlePreAndInGameSetting('InGame', { name, value })
          }
        />
      )
    }
  ];

  const location = useLocation();
  const { setBreadcrumb } = useBreadcrumb();

  useEffect(() => {
    let match = matchPath('/designer/:gameId/settings/*', location.pathname);
    if (match) {
      setBreadcrumb([{ text: 'Settings', to: match.pathnameBase }]);
    }

    return () => {
      setBreadcrumb([]);
    };
  }, [setBreadcrumb, location.pathname]);

  return (
    <div>
      <Toolbar
        title={'Settings'}
        buttonHelpSupport={{
          title: 'Game Designer settings guide',
          url: 'https://forum.catalystglobal.com/t/4528'
        }}
      />
      <Container className="setting-container pl-0" fluid>
        {isLoading ? (
          <div
            className={
              'd-flex flex-column align-items-center justify-content-center w-100 gap-2 my-4'
            }>
            <span className={'loader'}></span>
            <label className={'text-gray'}>Loading...</label>
          </div>
        ) : (
          <TabContentCG
            selected={selectedTab}
            handleSelect={handleTabSelect}
            items={items}
          />
        )}
      </Container>
    </div>
  );
};

export default GameSettings;
